import { useState } from 'react';
import { CheckIcon, RepeatIcon } from 'lucide-react';
import { toast } from 'sonner';
import { ProductImage } from '../../../ui/ProductImage';
import { useCatalog } from '../../../contexts/CatalogContext';
import { useSignedInAccount } from '../../../contexts/AccountContext';
import { useOverlayActions } from '../../../contexts/StoreContext';
import { policyCopy } from '../../../data/policies';
import { plural } from '../../../core/format/text';
import { refillableFromOrders } from '../../../utils/account';
import { btnPrimary } from '../../../ui/styles';
import { accountHeading } from '../../../ui/sections';

/**
 * Nog geen (of een gestopt) abonnement: de producten die ze al gebruikt,
 * aan te vinken en in één tik automatisch bij te vullen. Staat op Thuis (#levering).
 */
export function SubscribePrompt() {
  const {
    account,
    subscription
  } = useSignedInAccount();
  const {
    productById
  } = useCatalog();
  const {
    openSubscriptionBuilder
  } = useOverlayActions();
  const options = refillableFromOrders(account, productById);
  const shown = options;
  // Alleen wat ze ziet staat aangevinkt: nooit een product in het abonnement dat buiten beeld viel.
  const [picked, setPicked] = useState<string[]>(() => shown.map(p => p.id));
  const [busy, setBusy] = useState(false);
  if (!options.length) return null;
  const restarting = account.subscription?.status === 'gestopt';
  const toggle = (id: string) => setPicked(l => l.includes(id) ? l.filter(x => x !== id) : [...l, id]);
  const chosen = picked.filter(id => shown.some(p => p.id === id));
  const start = async () => {
    setBusy(true);
    const ok = await subscription({
      type: 'start',
      productIds: chosen
    });
    setBusy(false);
    if (!ok) return;
    toast.success(restarting ? 'Je abonnement loopt weer' : 'Je Bewust Abonnement staat klaar', {
      description: `${policyCopy.firstDelivery}. Aanpassen kan tot de dag ervoor.`
    });
  };
  return <section aria-labelledby="subscribe-title" className="bg-shell p-5 ring-1 ring-line sm:p-6" data-id="element-6327" data-mp="18saopflugrb9">
      <p className="flex items-center gap-2 text-[13px] text-muted" data-id="element-6328" data-mp="10rl5p9d9i5gy">
        <RepeatIcon className="h-3.5 w-3.5 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-6329" data-mp="wpq381jlhm1l" />
        Automatisch bijvullen
      </p>
      <h2 id="subscribe-title" className={`mt-1 ${accountHeading}`} data-id="element-6330" data-mp="1apslvpzq8jhw">
        {restarting ? 'Weer automatisch laten komen?' : 'Nooit halverwege zonder.'}
      </h2>
      <p className="mt-2 text-[14px] leading-relaxed text-muted" data-id="element-6331" data-mp="1jp5t0c0dovzg">
        Altijd zonder verzendkosten. {policyCopy.subscriptionGift}. Opschuiven of pauzeren kan altijd, in één tik.
      </p>

      <ul className="mt-4 space-y-2" aria-label="Welke producten wil je laten bijvullen?" data-id="element-6332" data-mp="1tf7i974gwkfy">
        {shown.map(p => {
        const on = picked.includes(p.id);
        return <li key={p.id} data-id="element-6333" data-mp="23dod4f6j1j7a">
              <button type="button" role="checkbox" aria-checked={on} onClick={() => toggle(p.id)} className={`flex w-full items-center gap-3 p-2 pr-3 text-left ring-1 transition-shadow duration-150 ease-soft ${on ? 'ring-ink' : 'ring-line hover:ring-ink/40'}`} data-id="element-6334" data-mp="23cvfq4tgk61t">
                <span className="h-10 w-10 shrink-0 bg-sand" data-id="element-6335" data-mp="2lzmgwftg3cnh">
                  <ProductImage src={p.image} alt="" className="h-full w-full p-0.5" data-id="element-6336" data-mp="281ebrkm99518" />
                </span>
                <span className="min-w-0 flex-1 truncate text-[14px] text-ink" data-id="element-6337" data-mp="2czpllvcjvfjg">{p.name}</span>
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${on ? 'bg-ink text-shell' : 'ring-1 ring-line'}`} aria-hidden="true" data-id="element-6338" data-mp="1yatgo75siin2">
                  {on && <CheckIcon className="h-3 w-3" strokeWidth={2.5} data-id="element-6339" data-mp="1pchj1l6iakl4" />}
                </span>
              </button>
            </li>;
      })}
      </ul>
      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3" data-id="element-6340" data-mp="1fdihemk5rp7m">
        <button type="button" disabled={!chosen.length || busy} onClick={() => void start()} className={btnPrimary} data-id="element-6341" data-mp="1feac7v5xliwu">
          {restarting ? 'Opnieuw starten' : 'Start met bijvullen'}
          {chosen.length > 0 && <span className="text-shell/70" data-id="element-6342" data-mp="2nplmynza5mf0">· {plural(chosen.length, 'product', 'producten')}</span>}
        </button>
        {/* Iets anders dan wat ze al gebruikt (een heel protocol, een ander ritme): de samensteller. */}
        <button type="button" onClick={openSubscriptionBuilder} aria-haspopup="dialog" className="min-h-[44px] text-[14px] text-muted underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-6343" data-mp="28ymhhltqrdzn">
          Liever zelf samenstellen
        </button>
      </div>
    </section>;
}